
import { useScrollReveal } from '../../../hooks/useScrollReveal';

export default function WhyPalemia() {
  const { ref, isVisible } = useScrollReveal(0.1);

  const points = [
    {
      icon: 'ri-shield-check-line',
      title: 'Precision Engineering',
      desc: 'Every system is built with surgical accuracy to eliminate waste and maximize output.',
    },
    {
      icon: 'ri-flow-chart',
      title: 'Scalable Architecture',
      desc: 'Infrastructure designed to grow with your business, not against it.',
    },
    {
      icon: 'ri-lock-line',
      title: 'Operational Control',
      desc: 'Full visibility and command over every automated workflow and process.',
    },
  ];

  return (
    <section className="relative py-36 px-6 bg-slate-950 overflow-hidden">
      <div ref={ref} className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          {/* Left Side - Content */}
          <div className={`relative z-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="text-teal-400 text-xs font-medium tracking-widest uppercase mb-4 block">Why Palemia</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 tracking-tight leading-tight">
              Why Structured<br />Systems <span className="bg-gradient-to-r from-teal-400 to-teal-300 bg-clip-text text-transparent">Win</span>
            </h2>
            <p className="text-base text-gray-400 leading-relaxed mb-10 max-w-lg">
              Service industries operate at the foundation of the economy. They require precision, protection, and performance. Palemia AI builds intelligent frameworks that eliminate operational chaos and create scalable control.
            </p>

            {/* Feature Points */}
            <div className="space-y-5">
              {points.map((point, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                  style={{ transitionDelay: `${(index + 2) * 150}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-500/10 border border-teal-500/15 flex-shrink-0 mt-0.5">
                    <i className={`${point.icon} text-lg text-teal-400`}></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">{point.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - Visual */}
          <div className={`relative transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="relative w-full h-[520px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/6121e1899730449a3056f3a68341cefb.jpg"
                alt="AI system visualization"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-slate-950/30" />
              <div className="absolute inset-0 border border-teal-500/10 rounded-2xl" />
            </div>

            {/* Floating Stats Card */}
            <div className="absolute -bottom-6 -left-6 glass-card rounded-xl p-5 animate-float">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-500/15">
                  <i className="ri-bar-chart-grouped-line text-lg text-teal-400"></i>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">System Uptime</p>
                  <p className="text-teal-400 text-xs font-medium">99.9% Reliability</p>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 glass-card rounded-xl p-4 animate-float-delayed">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-500/15">
                  <i className="ri-check-double-line text-base text-teal-400"></i>
                </div>
                <span className="text-white text-xs font-semibold">AI-Verified</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
